import { UserRole } from '../models/api-types';

export type AppCapability =
  | 'dashboard:view'
  | 'exercises:view'
  | 'progress:view'
  | 'scores:view'
  | 'scores:create'
  | 'scores:edit'
  | 'scores:delete'
  | 'scores:download'
  | 'users:view'
  | 'users:create'
  | 'users:edit'
  | 'users:deactivate'
  | 'users:reset-password'
  | 'courses:view'
  | 'courses:create'
  | 'courses:edit'
  | 'courses:delete'
  | 'courses:assign'
  | 'exercises:assign'
  | 'categories:view'
  | 'categories:create'
  | 'categories:edit'
  | 'categories:delete'
  | 'categories:assign'
  | 'observations:edit'
  | 'observations:insert-game';

const ALL_CAPABILITIES: AppCapability[] = [
  'dashboard:view',
  'exercises:view',
  'progress:view',
  'scores:view',
  'scores:create',
  'scores:edit',
  'scores:delete',
  'scores:download',
  'users:view',
  'users:create',
  'users:edit',
  'users:deactivate',
  'users:reset-password',
  'courses:view',
  'courses:create',
  'courses:edit',
  'courses:delete',
  'courses:assign',
  'exercises:assign',
  'categories:view',
  'categories:create',
  'categories:edit',
  'categories:delete',
  'categories:assign',
  'observations:edit',
  'observations:insert-game',
];

export const ROLE_CAPABILITIES: Record<UserRole, ReadonlySet<AppCapability>> = {
  SUPERADMIN: new Set(ALL_CAPABILITIES.filter(c => c !== 'dashboard:view')),
  ADMIN: new Set(ALL_CAPABILITIES.filter(c => c !== 'dashboard:view')),
  MODERATOR: new Set([
    'exercises:view',
    'progress:view',
    'scores:view',
    'scores:create',
    'scores:edit',
    'scores:delete',
    'scores:download',
    'courses:view',
    'courses:create',
    'courses:edit',
    'courses:delete',
    'courses:assign',
    'exercises:assign',
    'categories:view',
    'categories:create',
    'categories:edit',
    'categories:delete',
    'categories:assign',
    'users:view',
    'users:create',
    'users:edit',
    'users:deactivate',
    'users:reset-password',
    'observations:edit',
    'observations:insert-game',
  ]),
  TEACHER: new Set([
    'exercises:view',
    'exercises:assign',
    'progress:view',
    'scores:view',
    'scores:download',
    'courses:view',
    'courses:assign',
    'categories:view',
    'categories:assign',
    'users:view',
    'observations:edit',
  ]),
  CREATOR: new Set([
    'dashboard:view',
    'exercises:view',
    'progress:view',
    'scores:view',
    'scores:create',
    'scores:download',
    'courses:view',
    'courses:create',
    'categories:view',
    'categories:create',
  ]),
  STUDENT: new Set([
    'dashboard:view',
    'exercises:view',
    'progress:view',
    'scores:view',
    'scores:download',
    'courses:view',
  ]),
};

export function hasCapability(
  role: UserRole | null | undefined,
  capability: AppCapability
): boolean {
  if (!role) {
    return false;
  }
  const capabilities = ROLE_CAPABILITIES[role];
  return capabilities ? capabilities.has(capability) : false;
}

/**
 * Whether the current role is allowed to see the "Información" record-metadata
 * box (createdAt / updatedAt) on detail/edit pages. Only ADMIN and SUPERADMIN
 * qualify — see issue #381.
 */
export function canSeeRecordMeta(role: UserRole | null | undefined): boolean {
  return role === 'ADMIN' || role === 'SUPERADMIN';
}
